import type { VAxios } from '../../define/axios/Axios';
import {
  ChangePasswordParams,
  CurrentUserInfo,
  LoginParams,
  LoginResultModel,
  RegisterParams,
  ResetParams,
  SendSmsCodeParams,
  SendSmsCodeRes,
  UserSettingsRes,
} from '../../entities/uc/user.entities';

/**
 * 内容中心接口配置
 */
export const createApi = (defHttp: VAxios) => ({
  /**
   * 获取当前用户信息
   * @returns
   */
  getCurrent: () => {
    return defHttp.get<CurrentUserInfo>({ url: `/uc/current` });
  },
  /**
   * 登录
   * @param {object} params loginInfo
   * @param {string} params.account 用户名或手机号
   * @param {string} params.password 密码或短信验证码
   * @param {string} params.service 登录系统
   * @param {string} params.type 登录方式,可用值:password,mobile
   * @param {number} params.userId 用户ID
   * @returns
   */
  login(params: LoginParams) {
    return defHttp.post<LoginResultModel>(
      {
        url: `/uc/login`,
        params: { ...params, service: 'aiad' },
      },
      {
        withToken: false,
      }
    );
  },
  /**
   * 登出
   * @returns
   */
  logout() {
    return defHttp.post({ url: `/uc/logout` });
  },
  /**
   * 重置密码
   * @param {object} params changePassword
   * @param {string} params.newPassword 新密码
   * @param {string} params.oldPassword 旧密码
   * @returns
   */
  changePassword(params: ChangePasswordParams) {
    return defHttp.post({ url: `/uc/password`, params });
  },
  /**
   * 获取当前用户拥有的权限code
   * @returns
   */
  permissions() {
    return defHttp.post({ url: `/uc/permissions` });
  },
  /**
   * 用户注册
   * @param {object} params registerUser
   * @param {string} params.confirmPassword 确认密码
   * @param {string} params.mobile 手机号
   * @param {string} params.name 昵称
   * @param {string} params.password 密码
   * @param {string} params.sms 短信验证码
   * @returns
   */
  register(params: RegisterParams) {
    return defHttp.post({ url: `/uc/register`, params });
  },
  /**
   * 重置密码
   * @param {object} params reset
   * @param {string} params.confirmPassword 确认密码
   * @param {string} params.mobile 手机号
   * @param {string} params.password 密码
   * @param {string} params.sms 短信验证码
   * @returns
   */
  reset(params: ResetParams) {
    return defHttp.post({ url: `/uc/reset`, params });
  },
  /**
   * 发送短信验证码
   * @param {object} params send
   * @param {string} params.mobile 手机号
   * @param {string} params.type 手机验证码类型,可用值:register,login,resetPasswd,allopatry
   * @param {number} params.userId 用户ID,优先级高于手机号
   * @returns
   */
  sendSmsCode(params: SendSmsCodeParams) {
    return defHttp.post<SendSmsCodeRes>({ url: `/uc/sms`, params });
  },

  // 切换租户
  selectOrg(params: { orgId: string; refreshToken: string }) {
    return defHttp.post({ url: `/uc/orgs/switch`, params });
  },

  // 成员退出组织
  postOrgsExit(params: { orgId: string }) {
    return defHttp.post({ url: `/uc/orgs/exit`, params });
  },

  // 查询组织管理员配置
  getOrgAdminConfig() {
    return defHttp.get({ url: `/uc/orgs/permissions/config` });
  },

  // 更新组织管理员配置
  updateOrgAdminConfig(params: { adminPerm: number }) {
    return defHttp.put({ url: `/uc/orgs/permissions/config`, params });
  },

  // 获取有权限code的租户
  getOrgs: (code: string) => {
    return defHttp.get<any[]>({
      url: `/uc/orgs/permissions/${code}`,
    });
  },

  // 获取用户设置信息
  userSettings() {
    return defHttp.get<UserSettingsRes>({ url: `/uc/users/settings` });
  },

  // 更新当前用户信息
  updateUserInfo(params: {
    gender: string;
    industry: string;
    logo: string;
    name: string;
    position: string;
    userId: number;
  }) {
    return defHttp.put({ url: `/uc/users/current`, params });
  },

  // 重置手机号
  resetMobile(params: { mobile?: string; smsCode?: string; mobileAreaCode?: string }) {
    return defHttp.post({ url: `/uc/users/reset-mobile`, params });
  },

  // 重置手机号-校验当前手机号短信验证码
  checkSmsCodeForResetMobile(params: { smsCode?: string }) {
    return defHttp.post({ url: `/uc/users/reset-mobile-check`, params });
  },

  // 重置手机号-发送新手机号短信验证码
  sendSmsCodeForResetNewMobile(params: { mobile?: string; mobileAreaCode?: string }) {
    return defHttp.post({ url: `/uc/users/reset-mobile/sms`, params });
  },

  // 重置手机号-发送当前手机号短信验证码
  sendSmsCodeForResetMobile() {
    return defHttp.post({ url: `/uc/users/reset-mobile/sms/current` });
  },

  // 重置密码
  changeCurrentPassword(params: { password: string; repeat: string }) {
    return defHttp.post({ url: `/uc/users/reset-passwd`, params });
  },

  // 重置密码-校验当前手机号短信验证码
  checkSmsCodeForResetPasswd(params: { smsCode: string }) {
    return defHttp.post({ url: `/uc/users/reset-passwd-check`, params });
  },

  // 重置密码-发送重置密码短信验证码
  sendSmsCodeForResetPassword() {
    return defHttp.post({ url: `/uc/users/reset-passwd/sms` });
  },

  // 解绑微信公众号
  unbindWechat() {
    return defHttp.delete({ url: `/uc/users/wechat` });
  },

  // 绑定微信公众号
  bindWechat(params: { id: string }) {
    return defHttp.post({ url: `/uc/users/wechat`, params });
  },

  // 重置超级管理员-发送重置超级管理员短信验证码（当前登录用户）
  sendSmsCodeForResetAdmin() {
    return defHttp.post({ url: `/uc/users/reset-admin/sms` });
  },

  // 重置超级管理员-校验当前手机号短信验证码（当前登录用户）
  checkSmsCodeForResetAdmin(params: { smsCode: string }) {
    return defHttp.post({ url: `/uc/users/reset-admin-check`, params });
  },

  // 修改超级管理员
  setSuperAdmin(params: { appCode?: string; userId?: string }) {
    return defHttp.post({ url: `/uc/users/reset-admin`, params });
  },

  // 设置下载水印选项
  setWatermark(params: { watermark: boolean | null }) {
    return defHttp.put({ url: `/uc/user/watermark`, params });
  },

  // 自主转化流程表单提交
  postUcOrgsPodConvertForm(params: {
    category?: string;
    companyName?: string;
    platform?: string;
    purpose?: string;
    source?: string;
    storeNum?: string;
    teamSize?: string;
  }) {
    return defHttp.post({
      url: `/uc/orgs/pod/convert-form`,
      params,
    });
  },
});

